import { useState, useEffect } from "react";
import { authFetch } from "../api";
import Header from "../components/Header";

export default function BuildingsPage() {
  const [loading, setLoading] = useState(true);
  const [buildings, setBuildings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sortField, setSortField] = useState("display_address");
  const [sortDirection, setSortDirection] = useState("asc");
  const [filters, setFilters] = useState({
    borough: "",
    hasDoorman: "",
    hasElevator: "",
  });
  // Neighborhood multi-select
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState([]);
  const [neighborhoodDropdownOpen, setNeighborhoodDropdownOpen] = useState(false);
  const limit = 20;

  // Load all neighborhoods on mount (not filtered by model_active)
  useEffect(() => {
    const loadNeighborhoods = async () => {
      try {
        const data = await authFetch("/buildings/neighborhoods");
        setNeighborhoods(data.neighborhoods);
      } catch (error) {
        console.error("Error loading neighborhoods:", error);
      }
    };
    loadNeighborhoods();
  }, []);

  useEffect(() => {
    loadBuildings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, sortField, sortDirection, filters, selectedNeighborhoods]);

  const loadBuildings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (search) params.append("search", search);
      if (sortField) {
        params.append("sort_by", sortField);
        params.append("sort_dir", sortDirection);
      }
      if (selectedNeighborhoods.length > 0) {
        params.append("neighborhood_ids", selectedNeighborhoods.join(","));
      }
      if (filters.borough) params.append("borough", filters.borough);
      if (filters.hasDoorman !== "") params.append("has_doorman", filters.hasDoorman);
      if (filters.hasElevator !== "") params.append("has_elevator", filters.hasElevator);

      const response = await authFetch(`/buildings/?${params}`);
      setBuildings(response.buildings);
      setTotal(response.total);
    } catch (error) {
      console.error("Error loading buildings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setPage(1);
  };

  const loadBuildingDetail = async (buildingId) => {
    setDetailLoading(true);
    try {
      const response = await authFetch(`/buildings/${buildingId}`);
      setSelectedBuilding(response);
    } catch (error) {
      console.error("Error loading building detail:", error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      borough: "",
      hasDoorman: "",
      hasElevator: "",
    });
    setSelectedNeighborhoods([]);
    setSearchInput("");
    setSearch("");
    setPage(1);
  };

  const totalPages = Math.ceil(total / limit);

  const boroughName = (borough) => {
    const names = {
      1: "Manhattan",
      2: "Bronx",
      3: "Brooklyn",
      4: "Queens",
      5: "Staten Island",
    };
    return names[borough] || "-";
  };

  return (
    <>
      <Header showLogout={true} />
      <div style={{ padding: "40px", maxWidth: "1400px", margin: "0 auto" }}>
        <h1 style={{ marginBottom: "10px" }}>Buildings</h1>
        <p style={{ color: "#666", marginBottom: "30px" }}>
          Search buildings and add notes for the rent prediction agent
        </p>

        {/* Search and Filters */}
        <form onSubmit={handleSearch} style={{ marginBottom: "24px" }}>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <input
              type="text"
              placeholder="Search by address or building name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{
                padding: "10px 16px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                fontSize: "14px",
                width: "300px",
              }}
            />
            {/* Neighborhood Multi-Select Dropdown */}
            <div style={{ position: "relative" }}>
              <div
                onClick={() => setNeighborhoodDropdownOpen(!neighborhoodDropdownOpen)}
                style={{
                  padding: "10px 16px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  fontSize: "14px",
                  backgroundColor: selectedNeighborhoods.length > 0 ? "#e3f2fd" : "#fff",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  minWidth: "180px",
                }}
              >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                  {selectedNeighborhoods.length === 0
                    ? "All Neighborhoods"
                    : selectedNeighborhoods.length === 1
                    ? neighborhoods.find(n => n.id === selectedNeighborhoods[0])?.name || "1 neighborhood"
                    : `${selectedNeighborhoods.length} neighborhoods`}
                </span>
                <span style={{ color: "#666" }}>{neighborhoodDropdownOpen ? "▲" : "▼"}</span>
              </div>
              {neighborhoodDropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    backgroundColor: "#fff",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    zIndex: 1000,
                    maxHeight: "300px",
                    overflowY: "auto",
                    minWidth: "220px",
                  }}
                >
                  {/* Clear button */}
                  <div style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedNeighborhoods([]);
                      }}
                      style={{
                        padding: "4px 8px",
                        fontSize: "11px",
                        backgroundColor: "#f5f5f5",
                        border: "1px solid #ddd",
                        borderRadius: "3px",
                        cursor: "pointer",
                      }}
                    >
                      Clear All
                    </button>
                  </div>
                  {/* Neighborhood list */}
                  {[...neighborhoods].sort((a, b) => a.name.localeCompare(b.name)).map((n) => (
                    <label
                      key={n.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 12px",
                        cursor: "pointer",
                        backgroundColor: selectedNeighborhoods.includes(n.id) ? "#e3f2fd" : "transparent",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={selectedNeighborhoods.includes(n.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedNeighborhoods([...selectedNeighborhoods, n.id]);
                          } else {
                            setSelectedNeighborhoods(selectedNeighborhoods.filter(x => x !== n.id));
                          }
                        }}
                        style={{ marginRight: "8px" }}
                      />
                      <span>{n.name}</span>
                    </label>
                  ))}
                </div>
              )}
              {/* Click outside to close */}
              {neighborhoodDropdownOpen && (
                <div
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 999,
                  }}
                  onClick={() => setNeighborhoodDropdownOpen(false)}
                />
              )}
            </div>
            <select
              value={filters.borough}
              onChange={(e) => handleFilterChange("borough", e.target.value)}
              style={selectStyle}
            >
              <option value="">All Boroughs</option>
              <option value="1">Manhattan</option>
              <option value="3">Brooklyn</option>
              <option value="4">Queens</option>
              <option value="2">Bronx</option>
              <option value="5">Staten Island</option>
            </select>
            <select
              value={filters.hasDoorman}
              onChange={(e) => handleFilterChange("hasDoorman", e.target.value)}
              style={selectStyle}
            >
              <option value="">Doorman</option>
              <option value="true">Has Doorman</option>
              <option value="false">No Doorman</option>
            </select>
            <select
              value={filters.hasElevator}
              onChange={(e) => handleFilterChange("hasElevator", e.target.value)}
              style={selectStyle}
            >
              <option value="">Elevator</option>
              <option value="true">Has Elevator</option>
              <option value="false">No Elevator</option>
            </select>
            <button
              type="submit"
              style={{
                padding: "10px 20px",
                backgroundColor: "#000",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              Search
            </button>
            {(search || selectedNeighborhoods.length > 0 || filters.borough || filters.hasDoorman || filters.hasElevator) && (
              <button
                type="button"
                onClick={clearFilters}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#f5f5f5",
                  color: "#333",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                Clear
              </button>
            )}
          </div>
        </form>

        {/* Results count */}
        <div style={{ marginBottom: "16px", color: "#666", fontSize: "14px" }}>
          {total.toLocaleString()} building{total !== 1 ? "s" : ""} found
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            Loading buildings...
          </div>
        ) : (
          <>
            {/* Buildings Table */}
            <div
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                overflow: "hidden",
                marginBottom: "24px",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "14px",
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#f5f5f5" }}>
                    <SortableHeader
                      label="Address"
                      field="display_address"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <th style={thStyle}>Building Name</th>
                    <SortableHeader
                      label="Neighborhood"
                      field="neighborhood"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <th style={thStyle}>Borough</th>
                    <SortableHeader
                      label="Units"
                      field="total_units"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Year Built"
                      field="year_built"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <th style={thStyle}>Doorman</th>
                    <th style={thStyle}>Elevator</th>
                    <th style={thStyle}>Listings</th>
                    <SortableHeader
                      label="Notes"
                      field="feedback_count"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                  </tr>
                </thead>
                <tbody>
                  {buildings.map((b) => (
                    <tr
                      key={b.id}
                      style={{
                        borderBottom: "1px solid #e0e0e0",
                        cursor: "pointer",
                        backgroundColor:
                          selectedBuilding?.id === b.id ? "#e3f2fd" : "transparent",
                      }}
                      onClick={() => loadBuildingDetail(b.id)}
                      onMouseEnter={(e) => {
                        if (selectedBuilding?.id !== b.id) {
                          e.currentTarget.style.backgroundColor = "#fafafa";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedBuilding?.id !== b.id) {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }
                      }}
                    >
                      <td style={tdStyle}>
                        <strong>{b.display_address || "-"}</strong>
                      </td>
                      <td style={tdStyle}>{b.building_name || "-"}</td>
                      <td style={tdStyle}>{b.neighborhood || "-"}</td>
                      <td style={tdStyle}>{boroughName(b.borough)}</td>
                      <td style={tdStyle}>{b.total_units || "-"}</td>
                      <td style={tdStyle}>{b.year_built || "-"}</td>
                      <td style={tdStyle}>
                        <BooleanBadge value={b.doorman} />
                      </td>
                      <td style={tdStyle}>
                        <BooleanBadge value={b.elevator} />
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: "flex", gap: "8px" }}>
                          {b.active_sale_listings > 0 && (
                            <span style={listingBadgeStyle("sale")}>
                              {b.active_sale_listings} sale
                            </span>
                          )}
                          {b.active_rental_listings > 0 && (
                            <span style={listingBadgeStyle("rental")}>
                              {b.active_rental_listings} rental
                            </span>
                          )}
                          {b.active_sale_listings === 0 && b.active_rental_listings === 0 && "-"}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        {b.feedback_count > 0 ? (
                          <span
                            style={{
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "12px",
                              fontWeight: "500",
                              backgroundColor: "#e3f2fd",
                              color: "#1565c0",
                            }}
                          >
                            {b.feedback_count}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  style={{
                    ...paginationBtnStyle,
                    opacity: page === 1 ? 0.5 : 1,
                    cursor: page === 1 ? "not-allowed" : "pointer",
                  }}
                >
                  Previous
                </button>
                <span style={{ color: "#666" }}>
                  Page {page.toLocaleString()} of {totalPages.toLocaleString()}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  style={{
                    ...paginationBtnStyle,
                    opacity: page === totalPages ? 0.5 : 1,
                    cursor: page === totalPages ? "not-allowed" : "pointer",
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {/* Detail Panel */}
        {selectedBuilding && (
          <BuildingDetailPanel
            building={selectedBuilding}
            loading={detailLoading}
            onClose={() => setSelectedBuilding(null)}
            onFeedbackAdded={() => {
              loadBuildingDetail(selectedBuilding.id);
              loadBuildings();
            }}
            boroughName={boroughName}
          />
        )}
      </div>
    </>
  );
}

function BuildingDetailPanel({
  building,
  loading,
  onClose,
  onFeedbackAdded,
  boroughName,
}) {
  const [noteText, setNoteText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;

    setSubmitting(true);
    try {
      await authFetch(`/buildings/${building.id}/notes`, {
        method: "POST",
        body: JSON.stringify({ note: noteText }),
      });
      setNoteText("");
      onFeedbackAdded();
    } catch (error) {
      console.error("Error adding note:", error);
      alert("Failed to add note");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Are you sure you want to delete this note?")) return;

    try {
      await authFetch(`/buildings/${building.id}/notes/${noteId}`, {
        method: "DELETE",
      });
      onFeedbackAdded();
    } catch (error) {
      console.error("Error deleting note:", error);
      alert("Failed to delete note");
    }
  };

  if (loading) {
    return (
      <div style={panelOverlayStyle}>
        <div style={panelStyle}>
          <div style={{ textAlign: "center", padding: "40px" }}>
            Loading building details...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={panelOverlayStyle} onClick={onClose}>
      <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
            paddingBottom: "16px",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>{building.display_address}</h2>
            {building.building_name && (
              <p style={{ margin: "4px 0 0", color: "#666" }}>{building.building_name}</p>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: "#666",
            }}
          >
            &times;
          </button>
        </div>

        {/* Location */}
        <div style={{ marginBottom: "24px" }}>
          <h3 style={sectionHeaderStyle}>Location</h3>
          <div style={gridStyle}>
            <div style={fieldStyle}>
              <span style={labelStyle}>Full Address</span>
              <span>{building.full_address || "-"}</span>
            </div>
            <div style={fieldStyle}>
              <span style={labelStyle}>Neighborhood</span>
              <span>{building.neighborhood || "-"}</span>
            </div>
            <div style={fieldStyle}>
              <span style={labelStyle}>Borough</span>
              <span>{boroughName(building.borough)}</span>
            </div>
            <div style={fieldStyle}>
              <span style={labelStyle}>Postal Code</span>
              <span>{building.postal_code || "-"}</span>
            </div>
            {building.zone_name && (
              <div style={fieldStyle}>
                <span style={labelStyle}>Zone</span>
                <span>{building.zone_name} (Score: {building.zone_score})</span>
              </div>
            )}
            {building.location_score && (
              <div style={fieldStyle}>
                <span style={labelStyle}>Location Score</span>
                <span>{building.location_score.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Building Info */}
        <div style={{ marginBottom: "24px" }}>
          <h3 style={sectionHeaderStyle}>Building Details</h3>
          <div style={gridStyle}>
            <div style={fieldStyle}>
              <span style={labelStyle}>Total Units</span>
              <span>{building.total_units || "-"}</span>
            </div>
            <div style={fieldStyle}>
              <span style={labelStyle}>Total Floors</span>
              <span>{building.total_floors || "-"}</span>
            </div>
            <div style={fieldStyle}>
              <span style={labelStyle}>Year Built</span>
              <span>{building.year_built || "-"}</span>
            </div>
            {building.year_renovated && (
              <div style={fieldStyle}>
                <span style={labelStyle}>Year Renovated</span>
                <span>{building.year_renovated}</span>
              </div>
            )}
            <div style={fieldStyle}>
              <span style={labelStyle}>Ownership Type</span>
              <span>{building.ownership_type || "-"}</span>
            </div>
            <div style={fieldStyle}>
              <span style={labelStyle}>Building Type</span>
              <span>{building.building_type || "-"}</span>
            </div>
          </div>
        </div>

        {/* Amenities */}
        <div style={{ marginBottom: "24px" }}>
          <h3 style={sectionHeaderStyle}>Amenities</h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            <AmenityBadge label="Doorman" value={building.doorman} detail={building.doorman_coverage} />
            <AmenityBadge label="Concierge" value={building.concierge} />
            <AmenityBadge label="Elevator" value={building.elevator} detail={building.elevators ? `${building.elevators} elevators` : null} />
            <AmenityBadge label="Laundry" value={building.laundry} />
            <AmenityBadge label="Storage" value={building.storage} />
            <AmenityBadge label="Gym" value={building.healthclub} />
            <AmenityBadge label="Pool" value={building.pool} />
            <AmenityBadge label="Courtyard" value={building.courtyard} />
            <AmenityBadge label="Roof Deck" value={building.roof_deck} />
            {building.pets_allowed && (
              <span style={{ ...amenityStyle, backgroundColor: "#e8f5e9", color: "#2e7d32" }}>
                Pets: {building.pets_allowed}
              </span>
            )}
          </div>
        </div>

        {/* Distance Info */}
        {(building.dist_to_subway || building.dist_to_park) && (
          <div style={{ marginBottom: "24px" }}>
            <h3 style={sectionHeaderStyle}>Proximity</h3>
            <div style={gridStyle}>
              {building.dist_to_subway && (
                <div style={fieldStyle}>
                  <span style={labelStyle}>Distance to Subway</span>
                  <span>{(building.dist_to_subway / 1609.34).toFixed(2)} mi ({Math.round(building.dist_to_subway)} m)</span>
                </div>
              )}
              {building.dist_to_park && (
                <div style={fieldStyle}>
                  <span style={labelStyle}>Distance to Park</span>
                  <span>{(building.dist_to_park / 1609.34).toFixed(2)} mi ({Math.round(building.dist_to_park)} m)</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Active Listings */}
        <div style={{ marginBottom: "24px" }}>
          <h3 style={sectionHeaderStyle}>Active Listings</h3>
          <div style={{ display: "flex", gap: "16px" }}>
            <div style={{ ...statBoxStyle, borderColor: "#1976d2" }}>
              <div style={{ fontSize: "24px", fontWeight: "600", color: "#1976d2" }}>
                {building.active_sale_listings}
              </div>
              <div style={{ fontSize: "12px", color: "#666" }}>For Sale</div>
            </div>
            <div style={{ ...statBoxStyle, borderColor: "#2e7d32" }}>
              <div style={{ fontSize: "24px", fontWeight: "600", color: "#2e7d32" }}>
                {building.active_rental_listings}
              </div>
              <div style={{ fontSize: "12px", color: "#666" }}>For Rent</div>
            </div>
          </div>
        </div>

        {/* Building Notes Section */}
        <div style={{ marginBottom: "24px" }}>
          <h3 style={sectionHeaderStyle}>
            Building Notes ({building.feedback?.length || 0})
          </h3>
          <p style={{ fontSize: "13px", color: "#666", marginBottom: "16px" }}>
            These notes are used by the AI agent when predicting rent for units in this building.
          </p>

          {/* Add Note Form */}
          <form onSubmit={handleAddNote} style={{ marginBottom: "16px" }}>
            <div style={{ marginBottom: "12px" }}>
              <textarea
                placeholder="Add a note about this building (e.g., 'Building has excellent roof deck with Manhattan views', 'Lobby recently renovated')..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  fontSize: "14px",
                  minHeight: "80px",
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <button
              type="submit"
              disabled={!noteText.trim() || submitting}
              style={{
                padding: "10px 20px",
                backgroundColor: noteText.trim() ? "#1976d2" : "#ccc",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: noteText.trim() ? "pointer" : "not-allowed",
              }}
            >
              {submitting ? "Adding..." : "Add Note"}
            </button>
          </form>

          {/* Existing Notes */}
          {building.feedback?.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {building.feedback.map((note) => (
                <div
                  key={note.id}
                  style={{
                    padding: "12px 16px",
                    backgroundColor: "#f9f9f9",
                    borderRadius: "6px",
                    borderLeft: "3px solid #1976d2",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "14px", marginBottom: "8px" }}>{note.reasoning}</div>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        {note.created_by_name || "Unknown"} &middot;{" "}
                        {new Date(note.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteNote(note.id)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#999",
                        cursor: "pointer",
                        fontSize: "18px",
                        padding: "0 4px",
                      }}
                      title="Delete note"
                    >
                      &times;
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BooleanBadge({ value }) {
  if (value === null || value === undefined) return <span>-</span>;
  return (
    <span
      style={{
        padding: "2px 8px",
        borderRadius: "4px",
        fontSize: "11px",
        fontWeight: "500",
        backgroundColor: value ? "#e8f5e9" : "#f5f5f5",
        color: value ? "#2e7d32" : "#666",
      }}
    >
      {value ? "Yes" : "No"}
    </span>
  );
}

function AmenityBadge({ label, value, detail }) {
  if (!value) return null;
  return (
    <span style={amenityStyle}>
      {label}
      {detail && <span style={{ fontSize: "10px", marginLeft: "4px" }}>({detail})</span>}
    </span>
  );
}

function SortableHeader({ label, field, sortField, sortDirection, onSort }) {
  const isActive = sortField === field;

  return (
    <th
      style={{
        ...thStyle,
        cursor: "pointer",
        userSelect: "none",
      }}
      onClick={() => onSort(field)}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        {label}
        <span
          style={{
            fontSize: "10px",
            color: isActive ? "#1976d2" : "#999",
          }}
        >
          {isActive ? (sortDirection === "asc" ? "▲" : "▼") : "▲▼"}
        </span>
      </div>
    </th>
  );
}

// Styles
const thStyle = {
  textAlign: "left",
  padding: "12px 16px",
  fontWeight: "600",
  fontSize: "13px",
  color: "#333",
  borderBottom: "2px solid #e0e0e0",
};

const tdStyle = {
  padding: "12px 16px",
};

const selectStyle = {
  padding: "10px 16px",
  border: "1px solid #ddd",
  borderRadius: "6px",
  fontSize: "14px",
  backgroundColor: "#fff",
  cursor: "pointer",
};

const paginationBtnStyle = {
  padding: "8px 16px",
  backgroundColor: "#f5f5f5",
  border: "1px solid #ddd",
  borderRadius: "6px",
  fontSize: "14px",
};

const panelOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  justifyContent: "flex-end",
  zIndex: 1000,
};

const panelStyle = {
  width: "650px",
  maxWidth: "90vw",
  height: "100%",
  backgroundColor: "#fff",
  padding: "24px",
  overflowY: "auto",
  boxShadow: "-4px 0 20px rgba(0, 0, 0, 0.1)",
};

const sectionHeaderStyle = {
  fontSize: "16px",
  fontWeight: "600",
  marginBottom: "12px",
  color: "#333",
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "12px",
};

const fieldStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
};

const labelStyle = {
  fontSize: "12px",
  color: "#666",
  fontWeight: "500",
};

const amenityStyle = {
  padding: "6px 12px",
  backgroundColor: "#e3f2fd",
  borderRadius: "16px",
  fontSize: "12px",
  color: "#1565c0",
};

const statBoxStyle = {
  padding: "16px 24px",
  borderRadius: "8px",
  border: "2px solid",
  textAlign: "center",
  minWidth: "100px",
};

const listingBadgeStyle = (type) => ({
  padding: "2px 8px",
  borderRadius: "4px",
  fontSize: "11px",
  fontWeight: "500",
  backgroundColor: type === "sale" ? "#e3f2fd" : "#e8f5e9",
  color: type === "sale" ? "#1565c0" : "#2e7d32",
});
